import { useRef, useCallback } from "react";

/**
 * Cinematic Tour hook.
 * Sequences teleports through a list of waypoints with a short dwell between
 * each. Frame capture is the parent's responsibility — this hook only owns
 * the tour state machine and an onDone callback.
 *
 * Wiring:
 *   const tour = useCinematicTour({ beginTeleport });
 *   tour.attachTeleportComplete(setOnTeleportComplete);
 *   // External entry points (exposed via ref):
 *   tour.playTour(waypoints, onDone);
 *   tour.stopTour();
 *
 * @param {object} deps
 *   beginTeleport — function from useTapToTeleport, starts a single waypoint glide.
 * @returns {object} { tourRef, playTour, stopTour, attachTeleportComplete }
 */
export default function useCinematicTour({ beginTeleport, teleportRef }) {
  const tourRef = useRef(null);

  const playTour = useCallback((waypoints, onDone) => {
    if (!Array.isArray(waypoints) || waypoints.length === 0) return;
    tourRef.current = {
      running: true,
      index: 0,
      waypoints: waypoints.slice(),
      onDone,
    };
    beginTeleport(waypoints[0]);
  }, [beginTeleport]);

  const stopTour = useCallback(() => {
    if (tourRef.current) tourRef.current.running = false;
    tourRef.current = null;
    if (teleportRef && teleportRef.current) teleportRef.current = null;
  }, [teleportRef]);

  // Wired into useTapToTeleport so we advance to the next waypoint when the
  // current teleport finishes.
  const attachTeleportComplete = useCallback((setOnTeleportComplete) => {
    setOnTeleportComplete(() => {
      const tour = tourRef.current;
      if (!tour || !tour.running) return;
      tour.index += 1;
      if (tour.index >= tour.waypoints.length) {
        tour.running = false;
        tour.onDone?.();
        tourRef.current = null;
      } else {
        // 250ms dwell between waypoints — gives the eye time to land.
        setTimeout(() => {
          if (tourRef.current && tourRef.current.running) {
            beginTeleport(tour.waypoints[tour.index]);
          }
        }, 250);
      }
    });
  }, [beginTeleport]);

  return { tourRef, playTour, stopTour, attachTeleportComplete };
}
